import type { ChangeEvent, FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { breakpoints, Button, Inline, Input, Stack, vars, Text, LinkButton } from '@etg/wings';
import { LoadingDots } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, cx, mediaQueries } from '@eti/styles';
import { useMutation } from '@apollo/client';
import { CheckCircle, Trash } from '@phosphor-icons/react';
import { constants } from '@eti/utils';
import { ADD_DISCOUNT, REMOVE_DISCOUNTS } from '../../../graphql/mutations';

const { MOUSE_FLOW_CLASS_NAMES } = constants;

const formStyles = css`
  padding: 8px 0;
`;

const discountStyles = css`
  align-items: center;
  justify-content: space-between;
`;

const discountCodeStyles = css`
  color: ${vars.colors.signal.positive.foreground};
`;

const inputStyles = css`
  min-width: 35ch;

  > input {
    height: 3.1rem;
  }
`;

const addButtonStyles = css`
  flex: 0 0 100%;

  @media ${mediaQueries.medium.up} {
    flex: 0 auto;
  }
`;

const removeButtonStyles = css`
  display: flex;
  gap: 4px;
`;

const dotsStyle = css`
  color: ${vars.colors.signal.positive.foreground};
`;

interface DiscountFormProps {
  discountAmount: number | null;
  isPriceChangeDeclined: boolean;
  refetchCart: () => Promise<void>;
  refetchPaymentQuery: () => Promise<void>;
}

interface RemoveDiscountsVariables {
  discountCode: string;
  removeDiscounts: {
    success: boolean;
  };
}

const DiscountForm = ({
  discountAmount,
  isPriceChangeDeclined,
  refetchPaymentQuery,
  refetchCart,
}: DiscountFormProps) => {
  const [discountCode, setDiscountCode] = useState<string>(
    sessionStorage.getItem('discountCode') || '',
  );

  const voucherInputRef = useRef<HTMLInputElement | null>(null);

  const [isDiscountCodeAdded, setIsDiscountCodeAdded] = useState(
    Boolean(discountCode) || Boolean(discountAmount),
  );

  const [fieldError, setFieldError] = useState('');

  const { t } = useTranslation();

  const refetch = () => {
    refetchPaymentQuery();
    refetchCart();
  };

  useEffect(() => {
    const storedDiscountCode = sessionStorage.getItem('discountCode');
    if (storedDiscountCode) {
      setDiscountCode(storedDiscountCode);
      setIsDiscountCodeAdded(true);
    } else {
      setDiscountCode('');
      setIsDiscountCodeAdded(false);
    }
  }, [discountAmount]);

  const [addDiscountCode, { loading: addDiscountLoading }] = useMutation(ADD_DISCOUNT, {
    onCompleted: ({ addDiscountCode: response }) => {
      if (response?.success) {
        refetch();
        setIsDiscountCodeAdded(true);
        sessionStorage.setItem(
          'discountCodeAmount',
          response.discountPrice?.price?.value?.toString() ?? '',
        );
        sessionStorage.setItem('discountCode', discountCode);
        setFieldError('');
      } else {
        setFieldError(response?.discountErrorMessage?.messages?.[0] ?? '');
        if (voucherInputRef.current) {
          voucherInputRef.current.focus();
        }
      }
    },
  });

  const [removeDiscounts, { loading: removeDiscountLoading }] =
    useMutation<RemoveDiscountsVariables>(REMOVE_DISCOUNTS, {
      onCompleted: ({ removeDiscounts: response }) => {
        if (response?.success) {
          refetch();
          setDiscountCode('');
          setIsDiscountCodeAdded(false);
          sessionStorage.removeItem('discountCode');
          sessionStorage.removeItem('discountCodeAmount');
          voucherInputRef.current?.focus();
        }
      },
    });

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setDiscountCode(target.value);
    setFieldError('');
  };

  const handleOnSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (discountCode) {
      addDiscountCode({ variables: { discountCode } });
    } else {
      setFieldError(t('Payment.Discount.Required.Text'));
      if (voucherInputRef.current) {
        voucherInputRef.current.focus();
      }
    }
  };

  const removeDiscount = () => {
    if (!removeDiscountLoading) {
      removeDiscounts({ variables: { discountCode } });
    }
  };

  useEffect(() => {
    if (discountCode && isPriceChangeDeclined) {
      addDiscountCode({ variables: { discountCode } });
    }
  }, [isPriceChangeDeclined, discountCode, addDiscountCode]);

  return (
    <form className={formStyles} onSubmit={handleOnSubmit}>
      {isDiscountCodeAdded ? (
        <Inline className={discountStyles}>
          <Inline
            data-testid="discount-code-success"
            noWrap
            role="alert"
            spacing={8}
            style={{ alignItems: 'center' }}
          >
            <CheckCircle
              aria-hidden
              color={vars.colors.signal.positive.foreground}
              size={20}
              weight="fill"
            />
            <Text
              aria-live="polite"
              as="span"
              className={discountCodeStyles}
              id="discount-code-success"
              role="alert"
              variant="14-bold"
            >
              {t('Payment.Discount.Checkbox.Label', discountCode)}
            </Text>
          </Inline>
          <LinkButton
            className={removeButtonStyles}
            data-testid="discount-code-remove-button"
            onClick={removeDiscount}
          >
            {removeDiscountLoading ? (
              <LoadingDots dotsStyle={dotsStyle} />
            ) : (
              <>
                <Trash size={20} />
                {t('Payment.Discount.RemoveButton.Label')}
              </>
            )}
          </LinkButton>
        </Inline>
      ) : (
        <Stack spacing={4}>
          <Input.Label htmlFor="discount-code">{t('Payment.Discount.Heading')}</Input.Label>
          <Inline align="start" collapseBelow={breakpoints._768} spacing={16}>
            <Stack spacing={4}>
              <Input
                aria-describedby="discount-code-success"
                className={cx(inputStyles, MOUSE_FLOW_CLASS_NAMES)}
                data-testid="discount-code-input"
                errorId="discount-code-error"
                hasError={Boolean(fieldError)}
                id="discount-code"
                name="discount-code"
                onChange={handleInputChange}
                placeholder={t('Payment.Discount.Heading')}
                ref={voucherInputRef}
                tabIndex={-1}
                value={discountCode}
              />
              {fieldError && (
                <Input.Meta
                  error={fieldError}
                  errorId="discount-code-error"
                  hasError={Boolean(fieldError)}
                />
              )}
            </Stack>
            <div>
              <Button
                className={addButtonStyles}
                data-testid="discount-code-button"
                type="submit"
                variant="secondary"
              >
                {addDiscountLoading ? (
                  <LoadingDots />
                ) : (
                  <Text as="span" variant="14-bold">
                    {t('Payment.Discount.Button.Label')}
                  </Text>
                )}
              </Button>
            </div>
          </Inline>
        </Stack>
      )}
    </form>
  );
};

export default DiscountForm;
